import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { environment } from 'environments/environment';

import {
  IClientAccountDetailsModel,
  IClientAccountListItemModel,
  IClientAccountLockReasonModel,
  IClientAccountLookupItemModel,
  IClientAccountServicesModel,
  ICreateClientAccountModel,
  IUpdateClientAccountAddressFlagsModel,
  IUpdateClientAccountContactDetailsModel,
  IUpdateClientAccountDetailsModel,
  IUpdateClientAccountLockModel,
  IUpdateClientAccountServicesModel,
  IUpdateClientBusinessAccountDetailsModel,
} from '../models/client-account.models';
import {
  IAddressModel,
  IPaginatedListViewSearchModel,
  KeyValuePairModel,
  PaginatedModel,
} from '../models/common.models';
import { FilterParameterService } from './filter-parameter.service';
import { FilterState } from './filter-state.service';

@Injectable({
  providedIn: 'root',
})
export class ClientAccountService {
  private searchEvent = new Subject<any>();

  searchEvent$ = this.searchEvent.asObservable();

  private get baseApiUrl(): string {
    return environment.userManagementUrl;
  }

  constructor(
    private http: HttpClient,
    private filterParameterService: FilterParameterService,
  ) {}

  getClientAccountsV1(
    isExcludeClosedAccounts: boolean,
    isExcludeGuestAccounts: boolean,
    searchModel: IPaginatedListViewSearchModel,
    filters?: FilterState[],
  ): Observable<PaginatedModel<IClientAccountListItemModel>> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts`;
    let params = new HttpParams().appendAll({
      isExcludeClosedAccounts: isExcludeClosedAccounts.toString(),
      isExcludeGuestAccounts: isExcludeGuestAccounts.toString(),
      filterText: searchModel.searchText,
      orderByColumn: searchModel.orderByColumn,
      orderByDirection: searchModel.orderByDirection,
      page: searchModel.page.toString(),
      pageSize: searchModel.pageSize.toString(),
    });

    params = this.filterParameterService.getFilterParameters(params, filters);

    return this.http.get<PaginatedModel<IClientAccountListItemModel>>(url, {
      params,
    });
  }

  getClientAccountsLookupV1(
    filterText: string,
    managedStorageClientsOnly: boolean = false,
  ): Observable<PaginatedModel<IClientAccountLookupItemModel>> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/Lookup`;
    const params = new HttpParams()
      .append('filterText', filterText)
      .append('managedStorageClientsOnly', managedStorageClientsOnly);

    return this.http.get<PaginatedModel<IClientAccountLookupItemModel>>(url, {
      params,
    });
  }

  getClientAccountDetailsV1(id: number): Observable<IClientAccountDetailsModel> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${id}`;
    return this.http.get<IClientAccountDetailsModel>(url);
  }

  getClientAccountServiceDetailsV1(id: number): Observable<IClientAccountServicesModel> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${id}/services`;
    return this.http.get<IClientAccountDetailsModel>(url);
  }

  getClientAccountDetailsByLabelCodeV1(labelCode: string): Observable<IClientAccountDetailsModel> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${labelCode}`;
    return this.http.get<IClientAccountDetailsModel>(url);
  }

  createClientAccountV1(model: ICreateClientAccountModel): Observable<number> {
    const url = `${environment.authUrl}/pendingSignup`;
    return this.http.post<number>(url, model);
  }

  updateClientAccountDetailsV1(id: number, model: IUpdateClientAccountDetailsModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${id}/details`;
    return this.http.put<any>(url, model);
  }

  updateClientAccountServicesV1(id: number, model: IUpdateClientAccountServicesModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${id}/services`;
    return this.http.put<any>(url, model);
  }

  updateClientAccountContactDetailsV1(id: number, model: IUpdateClientAccountContactDetailsModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${id}/contact`;
    return this.http.put<any>(url, model);
  }

  updateClientAccountAddressV1(clientId: number, addressId: number, model: IAddressModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/addresses/${addressId}`;
    return this.http.put<any>(url, model);
  }

  updateClientAccountAddressFlagsV1(
    clientId: number,
    addressId: number,
    flag: keyof IUpdateClientAccountAddressFlagsModel,
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/addresses/${addressId}`;

    const model: IUpdateClientAccountAddressFlagsModel = {};
    model[flag] = true;

    return this.http.patch<any>(url, model);
  }

  updateClientBusinessAccountDetailsV1(id: number, model: IUpdateClientBusinessAccountDetailsModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${id}/business-details`;
    return this.http.put<any>(url, model);
  }

  createClientAccountAddressV1(clientId: number, model: IAddressModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/addresses`;
    return this.http.post<any>(url, model);
  }

  deleteClientAccountAddressV1(clientId: number, addressId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/addresses/${addressId}`;
    return this.http.delete<any>(url);
  }

  getClientAccountsAddressesV1(clientId: number): Observable<IAddressModel[]> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/addresses/`;
    return this.http.get<IAddressModel[]>(url);
  }

  get titles() {
    return ['', 'Dr', 'Mr', 'Mrs', 'Miss', 'Ms', 'Prof', 'Rev', 'Sir'];
  }

  lockClientAccountV1(clientId: number, model: IUpdateClientAccountLockModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/lock`;
    return this.http.put<any>(url, model);
  }

  unlockClientAccountV1(clientId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/unlock`;
    return this.http.put<any>(url, {});
  }

  closeClientAccountV1(clientId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/${clientId}/close`;
    return this.http.put<void>(url, {});
  }

  getClientAccountLockReasonsV1(): Observable<IClientAccountLockReasonModel[]> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/lockReasons`;
    return this.http.get<IClientAccountLockReasonModel[]>(url);
  }

  getAddressFormatted(address: IAddressModel, includeRecipient: boolean) {
    if (!address) return '';

    return [
      [
        address.addressLine1,
        address.addressLine2 ?? null,
        [address.city, address.state, address.postcode].join(' '),
        address.country,
      ]
        .filter((x) => x != null)
        .join(', '),
      includeRecipient ? address.recipientName : null,
      includeRecipient ? address.recipientPhone : null,
    ]
      .filter((x) => x != null)
      .join(' • ');
  }

  getClientAccountStatusesV1(): Observable<KeyValuePairModel<number>[]> {
    const url = `${this.baseApiUrl}/v1/ClientAccounts/statuses`;
    return this.http.get<KeyValuePairModel<number>[]>(url);
  }
}
